<template>
<div class="series-selected-funds">
  <h4 class="series-selected-funds__header">Selected Funds</h4>
  <div class="series-selected-funds__sub-header">
    {{selectedFundsSubtitle}}
  </div>
  <div class="series-selected-funds__items">
    <template v-if="isLoading">
      <mds-loader />
    </template>
    <template v-else>
      <selected-box-item
        v-for="fund in sortedData"
        :key="fund.id"
        :element="fund"
        @item-removed="handleItemRemoved"
      >
        <span :title="fund.displayName">
          {{ fund.displayName }}
        </span>
      </selected-box-item>
    </template>
  </div>
  <div class="series-selected-funds__actions">
    <hr />
    <mds-button
      type="button"
      class="series-selected-funds__button-save-continue"
      variation="primary"
      :disabled="!isValidData"
      @click="handleFinalSaveAndContinue"
    >
      Save and Continue
    </mds-button>
  </div>
  <div class="fiduciary-connect-modal">
    <mds-modal
      title="Remove Fund Confirmation"
      id="series-selected-funds__remove-confirm-modal"
      width="600px"
      v-model="showConfirmationModal"
      :title-hidden="true"
      @afirmative="handleRemoveConfirmation"
      @negative="closeConfirmation"
    >
      <div class="remove-confirm-modal-content">
        <div class="remove-confirm-modal-content__modal-question">
          Are you sure you want to remove this fund from the series?
        </div>
        <div class="remove-confirm-modal-content__modal-action">
          <mds-button
            type="button"
            variation="secondary"
            class="remove-confirm-modal-content__affirmative-button"
            @click="(event) => handleRemoveConfirmation(event)"
          >
            Yes
          </mds-button>
          <mds-button
            type="button"
            variation="primary"
            class="remove-confirm-modal-content__negative-button"
            @click="(event) => closeConfirmation(event)"
          >
            No
          </mds-button>
        </div>
      </div>
    </mds-modal>
  </div>
</div>
</template>

<script>
import { find, uniqBy } from 'lodash';
import { mapActions } from 'vuex';
import MdsLoader from '@mds/loader';
import SelectedBoxItem from '@/components/molecules/SelectedBoxItem';
import { config } from '@/config/add-fund-to-series-steps.json';
import { modifyFundsInSeries, getSeriesListByClientId, createNewSeriesForFiduciaryClient } from '@/services/TargetSeries';
import ErrorNotificationMixin from '@/mixins/ErrorNotificationMixin';

export default {
  name: 'series-selected-funds',
  components: {
    MdsLoader,
    SelectedBoxItem,
  },
  mixins: [
    ErrorNotificationMixin({ component: 'SeriesListSelection' }),
  ],
  data() {
    return {
      showConfirmationModal: false,
      fundToRemove: null,
      requestControllerForAddingFundsToSeries: null,
      requestControllerForCreatingANewSeries: null,
      request: null,
    };
  },
  props: {
    series: {
      type: Object,
      required: true,
    },
    firstSelectedFund: {
      type: Object,
      required: false,
    },
    selectedFunds: {
      type: Array,
      required: true,
    },
    originalFunds: {
      type: Array,
      required: false,
    },
    removedFunds: {
      type: Array,
      required: false,
    },
    selectedFiduciaryClientId: {
      type: Number,
      required: true,
    },
    withConfirmation: {
      type: Boolean,
      default: true,
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
  },
  methods: {
    ...mapActions({
      closeNotification: 'closeNotification',
    }),
    async handleItemRemoved(fund) {
      this.fundToRemove = fund;
      await this.closeNotification();
      if (!this.withConfirmation) {
        return this.emitRemoveEvent();
      }
      this.openConfirmation();
      return false;
    },
    openConfirmation() {
      this.showConfirmationModal = true;
    },
    closeConfirmation() {
      this.showConfirmationModal = false;
    },
    handleRemoveConfirmation() {
      this.emitRemoveEvent();
      this.closeConfirmation();
    },
    emitRemoveEvent() {
      this.$emit('fund-removed', this.fundToRemove);
      return true;
    },
    createNewSeriesAndReturnTheId(seriesName) {
      // variable to store the response from the POST request
      let newSeriesData;

      // calling TargetSeries.createNewSeriesForFiduciaryClient method
      return createNewSeriesForFiduciaryClient(
        this.selectedFiduciaryClientId,
        seriesName,
      ).then((result) => {
        if (result.status.success) {
          // storing the new series data
          newSeriesData = result.data;
          // returning the id of newly created series
          return newSeriesData.id;
        }
        return newSeriesData;
      }).catch((error) => {
        const httpStatus = (error?.response.status) ? (error?.response.status) : error?.status;
        if (httpStatus === 401) {
          this.$router.push('/Login');
        }
      });
    },
    // eslint-disable-next-line consistent-return
    getTheSeriesId(seriesName) {
      // check if the selectedFiduciaryClientId matches with the selectedSeriesFiduciaryClientId
      if (this.fiduciaryClientId === this.selectedFiduciaryClientId) {
        return this.series.id;
      }

      // to store all the series which belong to a particular fiduciary client
      let selectedFiduciaryClientSeries;
      let selectedFiduciarySeriesDetail;

      // triggering the API to get all the series for the selected fiduciary client
      return getSeriesListByClientId(this.selectedFiduciaryClientId)
        .then((response) => {
          // storing the data value which will contain all the series
          selectedFiduciaryClientSeries = response.data;

          // checking if the series name exists in the series list of the selected fiduciary-client
          selectedFiduciarySeriesDetail = find(selectedFiduciaryClientSeries, (item) => item.seriesName === seriesName);
          // checking if we got something in the selectedFiduciarySeriesDetail
          if (selectedFiduciarySeriesDetail) {
            return selectedFiduciarySeriesDetail.id;
          }

          // as we didn't find a match for the series name in the current fiduciary client
          // we have to create a new series and get the ID
          return this.createNewSeriesAndReturnTheId(seriesName);
        })
        .catch((error) => {
          // notifying with a error message if the request fails
          this.notifyError({
            error,
            section: 'lifecycle:created',
            data: this.selectedClient,
            notificaction: {
              title: 'Series List Selection',
              message: `Fetching the series list process has presented an error. (Error: ${error.message})`,
            },
          });
        });
    },
    async handleFinalSaveAndContinue() {
      // getting the seriesId for the selected fiduciary client with the help of fiduciary name which was selected
      const seriesId = await this.getTheSeriesId(this.series.seriesName);

      // removed funds if any
      if (this.removedFunds.length) {
        try {
          await modifyFundsInSeries(this.selectedFiduciaryClientId, this.removedFunds, seriesId, 'delete');
        } catch (err) {
          // this will delete all funds from the series which were removed by the user
        }
      }

      let onlyNewFunds = uniqBy(this.selectedFunds, 'id');
      let originalFunds = uniqBy(this.originalFunds, 'id');
      if (this.originalFunds.length) {
        if (this.firstSelectedFund?.id) {
          // no need to included the default included fund
          originalFunds = originalFunds.filter((fund) => fund.id !== this.firstSelectedFund.id);
        }
        // this will return new funds in selectedFunds, which dont exists in originalFunds
        onlyNewFunds = onlyNewFunds.filter((e) => {
          return !originalFunds.some((item) => item.id === e.id);
        });
      }
      if (onlyNewFunds.length) {
        // calling TargetSeries.modifyFundsInSeries method
        modifyFundsInSeries(
          this.selectedFiduciaryClientId,
          onlyNewFunds,
          seriesId,
          'add',
        ).then((result) => {
          if (result.status.success) {
            // going back to one client view
            this.redirctBackToOneClientView();
          }
        }).catch((error) => {
          const httpStatus = (error?.response?.status) ? (error?.response.status) : error?.status;
          if (httpStatus === 401) {
            this.$router.push('/Login');
          }
        });
      } else {
        // going back to one client view
        this.redirctBackToOneClientView();
      }
    },
    redirctBackToOneClientView() {
      this.$router.push({
        name: 'clientGrid',
        params: { clientId: `${this.selectedFiduciaryClientId}` },
      });
      // force the grid to reload
      this.$store.dispatch('setReloadNeed', true);
      // showing the notification
      this.$store.dispatch('openNotification', {
        message: 'The series has been updated.',
      });
    },
    abortingTheApiCalls() {
      // aborting the api call which was initiated for adding fund to series
      this.requestControllerForAddingFundsToSeries && this.requestControllerForAddingFundsToSeries.abort();
    },
  },
  computed: {
    isValidData() {
      return Array.isArray(this.selectedFunds) && this.selectedFunds.length > 0;
    },
    selectedFundsSubtitle() {
      let text = config.selectedFundsSubtitle.newSeriesInClient;
      if (!this.series.isNew && this.series.belongsToSelectedClient) {
        text = config.selectedFundsSubtitle.existentSeries;
      }
      return text;
    },
    sortedData() {
      let sortedData = this.selectedFunds;
      if (this.firstSelectedFund?.id) {
        const isTheSelectedFundInTheList = this.selectedFunds.some((fund) => {
          return this.firstSelectedFund.id === fund.id;
        });

        // If the first selected fund is in the list of selected funds it will appear in the first position
        if (isTheSelectedFundInTheList) {
          sortedData = this.selectedFunds.filter((fund) => {
            return this.firstSelectedFund.id !== fund.id;
          });
          sortedData.unshift(this.firstSelectedFund);
        }
      }
      return sortedData;
    },
    fiduciaryClientId() {
      return this.series.fiduciaryClientId;
    },
  },
  beforeDestroy() {
    // this.abortingTheApiCalls();
  },
};
</script>

<style lang="scss" scoped>
.series-selected-funds {
  padding: $mds-space-4-x;
  min-height: 500px;
  width: calc(100% - 820px - 40px);
  background-color: $fc-selected-box-bg-color;
  display: flex;
  flex-direction: column;

  &__header {
    @include mds-level-4-heading();
    color: $fc-main-intr-process-sub-header-text-color;
    margin: 0;
  }

  &__sub-header {
    font-size: $fc-selected-box-sub-header-text-size;
    margin: $mds-space-1-and-a-half-x 0;
  }

  &__items {
    margin-top: $mds-space-1-and-a-half-x;
    flex: 1;
  }

  hr {
    height: 1px;
    background-color: $fc-selected-box-hr-color;
    border: none;
  }
}

.series-selected-funds__button-save-continue {
  display: block;
  margin: auto;
  margin-top: $mds-space-3-x;
}

.remove-confirm-modal-content {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: $fc-selected-funds-modal-min-height;

  &__modal-question {
    @include mds-level-3-heading();
    color: $fc-main-intr-process-sub-header-text-color;
    display: flex;
    justify-content: center;
    margin-bottom: $mds-space-inset-4-x;
    text-align: center;
    width: 60%;
  }

  &__modal-action {
    display: flex;
    justify-content: space-between;

    button {
      width: calc(4 * #{$mds-space-5-x});
      margin-bottom: $mds-space-3-x;
      &:first-child {
        margin-right: 20px;
      }
    }
  }
}
</style>
